/**
 * 基础类型定义
 */

// 2D向量
export interface Vector2 {
    x: number;
    y: number;
}

// 六边形坐标（轴向坐标系）
export interface HexCoord {
    u: number;  // 横向坐标
    v: number;  // 纵向坐标（步长为2）
}

// 拼图边的类型
export type EdgeType = 'flat' | 'tab' | 'blank';  // 平边/凸起/凹陷

// 边的方向（六边形的6个方向）
export enum EdgeDirection {
    Left = 0,       // 左
    TopLeft = 1,    // 左上
    TopRight = 2,   // 右上
    Right = 3,      // 右
    BottomRight = 4,// 右下
    BottomLeft = 5  // 左下
}

// 边的数据
export interface EdgeData {
    key: string;           // 边的唯一标识
    type: 'h' | 'v';       // 横向边/纵向边
    x: number;
    y: number;
    reverse: boolean;      // 是否反向绘制
    points?: Vector2[];    // 贝塞尔曲线控制点
}

// 拼图碎片状态
export interface PieceState {
    id: string;
    coord: HexCoord;           // 逻辑坐标
    position: Vector2;         // 屏幕位置
    rotation: number;          // 旋转角度（0, 60, 120, 180, 240, 300）
    isPlaced: boolean;         // 是否已放置到正确位置
    isInPocket: boolean;       // 是否在口袋中
    groupId?: string;          // 所属碎片组（已拼接的碎片共享同一组）
}

// 游戏配置
export interface GameConfig {
    rings: number;             // 网格层数
    tabSize: number;           // 凹凸大小 (0.15 - 0.35)
    jitter: number;            // 抖动系数 (0 - 0.13)
    seed: number;              // 随机种子
    imageSize: number;         // 图片尺寸（固定1024）
    enableWarp: boolean;       // 是否启用圆形扭曲
    enableRotation: boolean;   // 是否启用碎片旋转
}

// 游戏存档
export interface GameSave {
    version: string;
    timestamp: number;
    puzzleId?: string;         // 拼图 ID
    puzzleName?: string;       // 拼图名称
    config: GameConfig;
    pieces: PieceState[];
    elapsedTime: number;       // 游戏时长（毫秒）
    moveCount: number;         // 移动次数
    imageData?: string;        // 图片数据（base64）
}

// 粒子效果类型
export enum ParticleEffectType {
    SnapSuccess = 'snap_success',      // 吸附成功
    PieceComplete = 'piece_complete',  // 碎片归位
    GameComplete = 'game_complete'     // 游戏完成
}

// 输入事件
export interface InputEvent {
    type: 'start' | 'move' | 'end';
    position: Vector2;
    pointerId: number;
}

// 拖拽状态
export interface DragState {
    isDragging: boolean;
    pieceId: string | null;
    startPosition: Vector2;
    offset: Vector2;
}

// ==================== 智能布局相关类型 ====================

// 纹理复杂度等级
export enum ComplexityLevel {
    LOW = 'LOW',
    MEDIUM = 'MEDIUM',
    HIGH = 'HIGH'
}

// 象限定义
export enum Quadrant {
    TOP_LEFT = 0,
    TOP_RIGHT = 1,
    BOTTOM_LEFT = 2,
    BOTTOM_RIGHT = 3,
    EDGE_RING = -1  // 边缘碎片环
}

// 布局配置
export interface LayoutConfig {
    // 中央工作区占比（0-1）
    centralAreaRatio: number;

    // Edge Ring 宽度（像素）
    edgeRingWidth: number;

    // 象限边界模糊度（像素）
    quadrantFuzziness: number;

    // 碎片间最小距离
    minPieceDistance: number;

    // 是否启用视觉退让
    enableVisualDim: boolean;
}

// 碎片布局数据
export interface PieceLayoutData {
    pieceId: string;
    isEdge: boolean;
    complexityLevel: ComplexityLevel;
    homeQuadrant: Quadrant;
    initialPosition: Vector2;
    initialRotation: number;
}

// ==================== 主题相关类型 ====================

export interface ThemeConfig {
    name: string;
    backgroundColor: number; // Table/Sides
    workAreaColor: number;   // Board
    topBarColor: number;     // Top UI Bar Background
    bottomBarColor: number;  // Bottom UI Bar Background
    baseMapFillColor: number;
    baseMapStrokeColor: number;
    baseMapStrokeAlpha: number;
    baseMapStrokeWidth: number;
    baseMapScale: number; // 0.9 - 1.0 (for gap size)
    textColor: number;
}

export const THEMES: Record<string, ThemeConfig> = {
    ZEN_LIGHT: {
        name: 'Zen Light (禅意·白)', 
        backgroundColor: 0xD6D3D1, 
        workAreaColor: 0xFAFAF9,
        topBarColor: 0xECEAE9,
        bottomBarColor: 0xECEAE9,
        baseMapFillColor: 0xEAE7E0, 
        baseMapStrokeColor: 0xD6D1C7,
        baseMapStrokeAlpha: 0.8,
        baseMapStrokeWidth: 1,
        baseMapScale: 1,
        textColor: 0x4A4540
    },
    ZEN_DARK: {
        name: 'Zen Dark (禅意·黑)', 
        backgroundColor: 0x0C0A09, 
        workAreaColor: 0x1C1B19,
        topBarColor: 0x161412,
        bottomBarColor: 0x161412,
        baseMapFillColor: 0x242321, 
        baseMapStrokeColor: 0x4A4540, 
        baseMapStrokeAlpha: 0.8,
        baseMapStrokeWidth: 1,
        baseMapScale: 1,
        textColor: 0xDEDAD6
    },
    ZEN_TEA: {
        name: 'Zen Tea (禅意·茶)', 
        backgroundColor: 0xC4C1B4, 
        workAreaColor: 0xE6E4D8,
        topBarColor: 0xD6D3C4,
        bottomBarColor: 0xD6D3C4,
        baseMapFillColor: 0xD6D3C4, 
        baseMapStrokeColor: 0x6A7A6E, 
        baseMapStrokeAlpha: 0.5,
        baseMapStrokeWidth: 1.5,
        baseMapScale: 0.98,
        textColor: 0x4A4540
    }
};
